// Training SOP — assembled document.
//
// The single object both outputs read: the in-app Training page today, the Word
// export later. Adding a track means adding it here; nothing else needs to know.
import type { TrainingDoc, Track } from './types';
import { FOUNDATIONS } from './foundations';
import { PM_TRACK } from './tracks/pm';
import { PE_TRACK } from './tracks/pe';
import { ESTIMATOR_TRACK } from './tracks/estimator';
import { FIELD_LEADER_TRACK } from './tracks/field-leader';
import { OPS_TRACK } from './tracks/ops';
import { MANPOWER_TRACK } from './tracks/manpower';
import { PPR_TRACK } from './tracks/ppr';
import { ADMIN_TRACK } from './tracks/admin';
import { MODULES } from './modules';
import changeNotes from 'virtual:training-change-notes';
import { deriveRevision } from '../lib/revision';

// Worked out from docs/training-changes/, not typed here — see lib/revision.ts.
const { revision, revised } = deriveRevision(changeNotes);

/** Order matters — it's the order of the track picker and the Word contents. */
export const TRACKS: Track[] = [
  PM_TRACK,
  PE_TRACK,
  ESTIMATOR_TRACK,
  FIELD_LEADER_TRACK,
  OPS_TRACK,
  MANPOWER_TRACK,
  PPR_TRACK,
  ADMIN_TRACK,
];

export const TRAINING: TrainingDoc = {
  title: 'Project Tracker — Training Guide',
  /**
   * ⚠️ DO NOT EDIT `revision` OR `revised`, AND DO NOT ADD AN ENTRY BELOW.
   *
   * TrainingPage prints these as "Revision X · reviewed <date>", and the Word
   * export carries them as the controlled document's version. Leaving them
   * behind labels new material as an older revision, which is worse than having
   * no version at all — a reader who checks the date concludes the guide hasn't
   * been touched and stops trusting it.
   *
   * They are now DERIVED: 0.37 plus one for each note in docs/training-changes/,
   * dated by the newest note's filename (lib/revision.ts). To change the guide,
   * add a note there — see docs/training-changes/README.md. Every training PR
   * used to edit this comment, `revision`, and a fingerprint in access.spec.ts, so
   * the second to merge always conflicted and was renumbered (the 0.35 entry below
   * was written as 0.31 and renumbered after four other PRs took 0.31–0.34). A new
   * file cannot conflict.
   *
   * The released string is stored with each reader's ticks and compared by
   * equality to flag a stale one, so a value must never be reused for different
   * content: notes are append-only (CI refuses to delete or rename one).
   *
   * The history below, 0.1 through 0.37, is FROZEN and was written by hand. Later
   * revisions are not listed here; their notes are in docs/training-changes/.
   *
   * 0.37 (2026-10-07) — the company-wide Daily Logs tab (#617), a top-navigation
   * list of every job's logs, newest first, with date, status and search
   * filters. The PM track gains Finding a daily log across every job (open it,
   * widen the dates, show Drafts to chase unsubmitted logs, click through to the
   * log in its job) and the appendix gains a reference topic on the tab. Both
   * are hidden from field-leader-only readers, who have Projects alone and are
   * redirected from the tab, though they keep the Daily Logs tab inside each
   * job. Three facts are not guessable from the screen and are stated: it lists
   * logs that exist, so a day nobody started has no row and a missing day cannot
   * be found there; search reads only the job, the author and the Work summary
   * box; and it stops at the newest 500 logs. Scope: this tab, not a sweep of
   * the rest of the guide.
   * 0.36 (2026-10-07) — equipment rental agreements (#571, shipped in #597).
   * The Operations track's Equipment section gains Keeping the rental agreement
   * with the request: attaching, downloading and removing files in the edit
   * drawer's Rental Agreement & Files section, and setting the rate. Shown only
   * to the EquipmentManager and Admin roles, the app's own rule (Admin named
   * outright, since allows() never lets it stand in for a role), and only with
   * equipment.edit, which the drawer needs to open. Three facts are not
   * guessable from the screen and are stated: the files live in the Project
   * Tracker, not the Job Folder that RFI and submittal documents go to (0.11);
   * a file is added or removed at once, not on Save; and nobody else sees the
   * rate or the files — their saves leave the rate alone, but they can still
   * change the Billing Cycle and Quantity it is charged by, and delete the
   * request, which takes its files out of reach with no undo in the app. The
   * appendix's Equipment entry gains a topic for every other reader: the rate
   * is hidden from them rather than blank, the rate sheet's list prices they
   * can see are not it, and, if they can edit requests but the drawer shows
   * them no Rental Agreement & Files section, they should check with an
   * equipment manager before deleting one, which can carry an agreement they
   * cannot see. That last part is conditional on what the reader sees, because
   * the note also reaches read-only readers, who have no Delete, and equipment
   * managers and admins, who see the files. It is in the appendix rather than
   * the Operations track because PMs, who lost sight of the rate and can delete
   * requests, are not suggested that track. Written as 0.34 and renumbered
   * twice, as #605 and then #591 took 0.34 and 0.35 first. Scope: the rental
   * terms only; #597's Export to Excel on the Equipment page is not covered.
   * 0.35 (2026-10-07) — Weekly Report: the new tab after Overview, its two
   * Word exports, choosing which days go in the client copy, and uploading the
   * finalized reports week by week. Written as 0.31 and renumbered as Punch
   * List video clips, Admin-only New Project, the job reports and a Completed
   * job's forecast took 0.31–0.34 first.
   * 0.34 (2026-10-06) — a Completed job's forecast. Once Intacct marks a job
   * Completed, the Financials tab no longer carries the budget left on a cost
   * code as cost still to come: a code with no forecast of its own defaults to
   * $0 instead of its remaining budget (accounting's rule, controller,
   * 2026-10-06 — Completed means the PM has said the work is finished with no
   * open commitments). The PM's "Job financials and the forecast" topic said
   * clearing a box falls back to the remaining budget, which is no longer the
   * whole truth, and gains a warning saying what an unforecast code is assumed
   * to do and when that budget is released. A forecast someone has typed or
   * built up still counts. Scope: that one topic, not a sweep of the guide.
   * 0.33 (2026-10-05) — the job reports and Transfer Budget, ahead of the
   * financial rollout to estimators and PMs. The PM track gains Moving budget
   * between cost codes (under Watching the money) and a section of its own,
   * Reading the job reports, with a topic for the Job Cost Management Report
   * and one for the Man Hours Report; the Estimator track gains the same two
   * reports in a section of their own, framed as checking the job against the
   * estimate; the appendix gains a Financials topic on where Transfer Budget
   * lives, and two Reports (on a job) topics — each report is granted on its
   * own, and which report answers which question. The reports are a SECTION OF
   * THEIR OWN, not topics under Watching the money, because they need
   * reports.view plus a permission per report and ignore the company cost
   * setting, where that section needs financials.view and the setting. Four
   * facts are not guessable from the screen and are stated: Transfer Budget
   * only exists while Update Forecast is open (so it takes forecast access as
   * well as its own permission, and the topic is shown only to a reader holding
   * both — AccessRule gains allPermissions, every one required, because
   * anyPermission can only OR), a transfer is written the moment Apply is
   * pressed and Cancel does not undo it, a transfer changes the Project
   * Tracker's budget only so the Job Cost Management Report (which reads the
   * Intacct estimate directly) will not show it, and Man Hours estimated hours
   * are derived from labor dollars rather than typed. Scope: these two
   * features, not a sweep of the rest of the guide.
   * 0.32 (2026-10-05) — "+ New Project" is Admin-only (the server enforces it
   * too), so the PM track now says only an Admin can add a job by hand rather
   * than offering it to PMs as an exception. No other instruction changed.
   * 0.31 (2026-10-05) — Punch List: video clips on items.
   * 0.30 (2026-10-02) — month-end accruals on the Commitments tab: a PM topic
   * for entering them, and two Commitments module topics — what the schedule
   * is and is not (it feeds no other figure), and who can enter it.
   * 0.29 (2026-10-01) — Punch List: the close-out report, and what a closed-out
   * list keeps fixed (its photos, whether approval is required).
   * 0.28 (2026-09-30) — Punch List: changing many items at once from the
   * table.
   * 0.27 (2026-09-30) — Punch List: email text for sub sheets, and outdoor
   * high-contrast mode on the phone.
   * 0.26 (2026-09-30) — Punch List on a phone: grouped by room, the
   * step-by-step add, and verifying a fix on the spot.
   * 0.25 (2026-09-29) — Punch List: sub sheets, and that printing one doesn't
   * mark its New items sent until you say so.
   * 0.24 (2026-09-29) — Punch List: each job's own trades and hand-added
   * names in Assigned to, managed in Settings by the PM or PE.
   * 0.23 (2026-09-28) — the Punch List tab (#518): a module entry covering status
   * versus ball in court, the photo rules and publishing.
   * 0.22 (2026-09-25) — filing a signed Travel Acknowledgment in SharePoint.
   * The Crew Scheduling track's Travel section gains a topic for the card's
   * new File to SharePoint button: the PDF is the form as signed, in the
   * language signed; it saves with the scheduler's own access into a Travel
   * Acknowledgments folder created the first time; it is filed once; and what
   * to do when the upload worked but the record did not.
   * 0.21 (2026-09-24) — the travel payroll report. The Crew Scheduling
   * track's Travel section gains a topic for it: where it is (Gap Analysis,
   * beside the travel roster), that it lists rates and never amounts, the
   * Excel download, the weekly email to payroll and its re-send, and what
   * "Replaced by a later acknowledgment" means for pay. Shown to schedulers
   * and to the new `travel.payroll_report` permission (the HR role), so the
   * Payroll Specialist is taught the screen they are given.
   * 0.20 (2026-09-24) — the Travel Acknowledgment form gains the three
   * fields the approved form shows and the form did not: Site Address,
   * Regional Office (from Paycor's Assigned Regional Office) and Mobilization
   * Date. Each is required, so the refusal table gains four rows (an
   * unverified address, no mobilization and no planned start, no office, and
   * two offices for one employee ID), each with who fixes it. The re-issue
   * warning's list of what the form shows now names the address, the
   * mobilization date and the office, and stops citing the address as the
   * example of a job detail that needs no new link — that example became
   * wrong. A new watch-out says which mobilization the date is, and that a
   * sub-project takes its parent's address and, lacking its own, its parent's
   * mobilizations.
   * 0.19 (2026-09-24) — the Start ≠ Crew flag (#385, shipped in #540). When a
   * job's planned start and its first booked crew day disagree, the crew grid
   * and the job's Schedule & Dates card now both say so. One topic per owner,
   * because the flag exists precisely because each held half the answer: the
   * scheduler's (Crew Scheduling track, "When the crew and the planned start
   * disagree") covers the badge, the ⚠ Start ≠ Crew worklist chip — which SHOWS
   * ONLY, where every other chip on that row hides — and the export; the PM's
   * (PM track, "When the crew schedule disagrees with your start date") covers
   * the banner. The scheduler's is shown to manpower editors only (full or
   * fab), since it ends in moving crew and publishing; a read-only viewer of
   * the board learns what the badge means from the appendix instead.
   * Both say it never decides which date is right, and that a change
   * from the other person can take five minutes to appear. A new
   * OWNERSHIP.startAlignment names both owners. Also: the grid topic gains the
   * row links to each job; "A job is not on the grid" gains the chip as a
   * cause; the Needs Scheduling wording says "no crew booked in those weeks",
   * since a job can now sit there with crew booked later and a badge naming it;
   * and the appendix's Schedule entry gains a one-line reference.
   * Moving crew only counts once it is PUBLISHED — the flag reads published crew
   * — and both topics say so. The PM's also says to switch the crew schedule to
   * Fabrication or All Divisions on a fab job, because it opens on Self-Perform,
   * which leaves fab jobs off. And the scheduler's says to go by the badge, not
   * the row color: a flagged job that is also Needs Scheduling or maintenance
   * keeps its amber or blue.
   * Also #541, which merged the same day: the Projects list's column is now
   * "Planned Start" (the appendix's Projects entry says what it holds — the
   * planned start; the latest mobilization's start marked ↺ when it has one,
   * which includes a job's first mobilization, not only a remob; or, on a fab phase with
   * no planned start, its original start and then its actual, once some phase
   * has an end — and that the sort and start-date filter use it), and the
   * actual start moved from the Schedule & Dates card to a tile on the job's
   * Manpower tab (the appendix's Manpower entry says how that date is decided:
   * blank until the job starts, and a PM's clear on any phase keeps Paycor
   * hours from filling it, though a start entered on any phase still does).
   * Corrected while there: the PM's "Setting start and end dates" step named an
   * "Actual or Projected Start" that has not existed since #380 and never
   * mentioned Planned Start. It now uses the drawer's real labels, and splits
   * out Actual Start by job type — not typed in on a one-phase job, where Paycor
   * fills it and typing over it freezes it against later timecard fixes (except
   * after a PM's clear, which Paycor never undoes, so the real start is typed
   * in once work begins); entered by the PM per phase on a fabrication job,
   * where nothing fills it. And the
   * appendix's Manpower entry dropped "Nothing on this tab is edited by hand",
   * which had been false since the request card arrived.
   * Every reader-visible claim this revision adds was then checked against the
   * code in one pass: the grid never badges Complete, Canceled or archived
   * jobs (they still show there while crew is booked), so the card can flag a
   * job the grid does not; on a fab job the compared
   * planned start is the earlier phase's; the banner's link opens the grid,
   * not the job; and neither flag changes a date itself.
   * Scope: #540 and #541, plus those corrections. Not a full-guide sweep.
   * 0.18 (2026-09-23) — travel. The Crew Scheduling track gains a "Travel"
   * section for the travel roster (#396) and Travel Acknowledgments (#542),
   * neither of which the guide covered: reading the roster under Gap Analysis,
   * keeping it current, issuing a link from a job's Manpower tab, reading the
   * card's list (Not signed, signed, Replaced by a newer link, and the pay
   * warnings), and who fixes each refusal. The appendix's Manpower module
   * gains the card. Everything but the roster's read-only topic is gated on
   * `manpower.edit`, the only role responsible for travel acknowledgments
   * (product owner, 2026-09-22) — so a fab editor, who does not hold it, is
   * not taught a card they are never shown.
   * 0.17 (2026-09-21) — the guide no longer names an individual. Two lines in
   * the Estimator track's forecast-update topic named the manpower lead
   * personally, once for where an update goes and once for who reads the
   * "What changed" box; both now name the role, which is how every other track
   * already words it. No procedure changed — the same button sends the same
   * update to the same inbox. Reader-visible text did change, hence a revision
   * rather than a silent re-baseline. Prose that ships is published prose, and
   * a name in it is published with it (#528 follow-up).
   * 0.15 (2026-09-02) — the Manpower Request form is in the Tracker and the
   * Formstack Manpower Change Form is retired (#303, slices 1–6; #386; #420).
   * Four additions, one per reader who touches it: the PM raises crew requests
   * from a job's Manpower tab (PM track, "Running the work"); the estimator
   * sends SYSTAC forecast updates from the same button on a bid (Estimator
   * track, "From bid to job"); the scheduler decides them in a new REQUESTS
   * inbox on the Manpower page (Crew Scheduling track, new "Requests" section,
   * with the one rule that matters — Booked records crew already entered by
   * hand, never enters it); and the appendix's Manpower module gains the card.
   * A new OWNERSHIP.manpowerRequests names who raises, who books.
   * 0.14 (2026-08-31) — no changelog line was written for this bump; the
   * content diff is in git (PR #428 / #433 era).
   * 0.13 (2026-08-27) — a job that has not started is no longer judged on its
   * start date moving (issue #399). The health rule flagged 10 live
   * Preconstruction jobs At Risk for a start that had only been re-planned, so
   * it now applies once the job is in Construction. Both the trigger table and
   * a new watch-out say so, because the guide is where a PM goes to find out
   * why a dot is the color it is.
   * 0.12 (2026-08-21) — blanket POs belong to the ESTIMATOR, not the PM (issue
   * #368: primary estimator, secondary PM and PE). The topic had
   * been borrowing `OWNERSHIP.concreteMixDesigns`, which conflated two
   * different responsibilities that happen to share the QA/QC tab — approving
   * a Mix ID is the PM's call (Director of Preconstruction, 2026-07-29), while
   * the BPO is raised by whoever creates it in Sage. Split into a new
   * `concreteSupplierAndBpo` constant; the PM topic is reframed to checking
   * and chasing rather than originating, per the house rule for topics the
   * reader does not own; and the estimator track gains a topic of its own, so
   * the new owner is actually told it is theirs rather than the label moving
   * and nobody being informed. `pe-mix-not-approved` deliberately keeps
   * `concreteMixDesigns` — it really is about mix approval.
   * 0.11 (2026-08-20) — how documents attach to RFIs and submittals, prompted
   * by a real question from the field (#367) and the copy shipped in #370. The
   * guide had said nothing about it, which is why the question arose. Three
   * facts, none of them guessable from the screen: the Job Folder card is on
   * the saved RFI/submittal rather than the create form, because the file name
   * is built from its number; uploading is the RECORD, not the transmittal;
   * and outgoing mail needs the files attached in Outlook because Job Folder
   * links do not open for anyone outside E&W. Added to the PM and PE submittal
   * and RFI topics and to the Foundations "I can't upload a file" topic. The
   * Field Leader track frames attaching and sending as the PM's steps and asks
   * for the drawing or detail number in the question text — deliberately an
   * OWNERSHIP statement rather than "you cannot attach", because a field leader
   * who also holds an office grant (`projects.edit`) genuinely can, and
   * watch-out bullets carry no access rule of their own to gate on.
   * 0.10 (2026-08-18) — two new role tracks. (Dot-sequence versioning: 0.10
   * follows 0.9.) **Crew Scheduling** for ManpowerEditor and FabManpowerEditor —
   * one track for both, with the full-editor tools (Set up crew, row
   * housekeeping, the Field Leaders roster, Available MP / day) gated on
   * `manpower.edit` so a fabrication-scoped editor's copy shows only the moves
   * their role has, plus a scoping topic of their own. **PPR Coordinator** for
   * PprCoordinator — pipeline, the nine contacts, the generated (never sent)
   * emails, the 21-field Financial Data tab with the Accounting handoff, the
   * report template, and cancel/reopen/delete. Both roles previously fell to
   * "Foundations only" in the progress roll-up; with `suggestedFor` rules they
   * now get a real track and a real denominator. Also adds an OWNERSHIP entry:
   * PPR financials are Accounting's, coordinator assisting (issue #312).
   * 0.9 (2026-08-17) — US English throughout. The guide had been written in a
   * British register — "coloured dots", "Dot colours", "grey", "greyed out",
   * "catalogue", "labour", "itemise", "behaviour", "recognises",
   * "generalises", plus "tick"
   * for a checkbox and "straight away" — and an imported schedule was called a
   * "programme", which is British construction usage for the thing this company
   * calls a schedule. None of it matched the app's own wording: the UI and the
   * code say color and labor everywhere. NO INSTRUCTION CHANGED; this is
   * wording only. ⚠️ Scope: a spelling and vocabulary pass, NOT a content sweep
   * against features shipped since 0.8 — so this bump does not carry the
   * assurance that 0.8's did. Left alone deliberately: "Cancelled" on pour
   * cards, which is QA/QC's actual label (pourShared.tsx, PourScheduleGrid),
   * and the bid statuses in the estimator track, which originate in Bid Tracker.
   * Note the app is inconsistent here and the guide follows it rather than
   * correcting it — pour cards say "Cancelled", project/survey/equipment
   * statuses say "Canceled".
   * 0.8 (2026-08-09) — caught up with #309, which shipped the day before 0.7
   * and was missed by it. The company-wide crew schedule now opens on
   * Self-Perform (fabrication excluded), so a fab job reads as having no crew
   * until you change scope; and Projects filters now survive navigating into a
   * job and back, so a list can arrive already narrowed by something set an
   * hour ago — the reason a job "disappears" and an export comes back short.
   * Also fixes four places where single-asterisk emphasis printed as a literal
   * asterisk to every reader — only **bold** renders, in the app and in Word —
   * and a spec now fails on any lone asterisk rather than on the one instance
   * an unrelated test happens to trip over, which is how 0.3 left four behind.
   * Issued for the PM/estimator rollout ahead of the September go-live.
   * 0.7 (2026-08-06) — equipment statuses renamed: On Rent → On-site and
   * Returned → Complete (both also cover OWNED gear, which is never "on rent"),
   * and Approved retired as never-used. The Operations track described the old
   * workflow order and said Call Off / Extend appear while an item is "On Rent",
   * so a reader would have hunted for a status that no longer exists.
   * 0.6 (2026-08-06) — "Take me there →" now opens the tracker in its own tab
   * so the guide stays where you are reading it. Dropped the Training-guide
   * appendix entry's link to /training: it pointed at the page you are on, and
   * as a second-tab jump it opened a duplicate copy of the guide.
   * 0.5 (2026-08-04) — estimated labor hours are now TYPED by the estimator at
   * job set-up and flagged when missing; the weekly Intacct hours sync was removed
   * because its autocalculated figure ran low. Rewrote the estimator's projected-
   * hours topic (it described an override and a 'Reset to Intacct' button that no
   * longer exist) and told PMs whose job it is to chase.
   * 0.4 (2026-08-04) — field leaders can now submit RFIs, so their track has a
   * new section for it and the PM's RFI topic warns that an Open RFI they didn't
   * write is a question from the field awaiting sending. No change was needed
   * for contract value or change-order amounts being withheld from the field:
   * `fl-your-view` already promised "you will not see any dollar figures
   * anywhere", and PRs #290/#291 made the app finally match that promise.
   * 0.3 (2026-08-02) — added the screen-by-screen reference appendix and the
   * Word export; fixed nested emphasis that printed literal asterisks; gated
   * the appendix to match the real navigation guards.
   * 0.2 (2026-08-02) — corrected the forecast step, which #280 made wrong;
   * added the CTC buildup, concrete supplier/BPO, allocated indirect and the GL
   * drill-down; restricted the Administrator track to the Admin role.
   * 0.1 (2026-07-28) — first issue: foundations + all six role tracks.
   */
  revision,
  revised,
  foundations: FOUNDATIONS,
  tracks: TRACKS,
  // Module-by-module reference. Deliberately thin on procedure — see the note
  // at the top of modules.ts for why duplicating task steps here is the thing
  // that makes an appendix rot.
  modules: MODULES,
  modulesTitle: 'Appendix — screen by screen',
};
